import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { FiCheckCircle, FiAlertTriangle } from "react-icons/fi";
import { useAuth } from "../../contexts/AuthContext";
import { usePageTitle } from "../../hooks/usePageTitle";
import { parseExtensionAuthParams } from "../../utils/extensionAuthParams";

// LEGACY — this page is unreachable and stays that way. Login emails carry a
// 6-digit code, never a link: authService mints a token hash for the DB but never
// passes the token to the email layer, and the template renders no URL.
//
// Do NOT re-enable token links here. This page hardcodes navigate("/dashboard")
// and ignores returnTo, so a link that opens in a fresh tab would silently strand
// the /oauth/consent MCP connect flow — the user would sign in and land on the
// dashboard with the connector's authorize params gone.

const MagicLinkCallback = () => {
  const { completeMagicLogin } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [status, setStatus] = useState("verifying");
  const [error, setError] = useState("");
  const verificationPromises = useRef({});
  const redirectTimer = useRef(null);
  const redirectAnimationFrame = useRef(null);
  const [redirectProgress, setRedirectProgress] = useState(0);
  const extensionAuthOptions = useMemo(() => {
    return parseExtensionAuthParams(location.search);
  }, [location.search]);
  const isExtensionAuthFlow = Boolean(extensionAuthOptions);

  // Set page title based on status
  usePageTitle(status === "success" ? "Login Successful" : status === "error" ? "Login Failed" : "Verifying Login");

  const getFriendlyVerifyError = (message = "") => {
    const normalized = message.toLowerCase();
    if (normalized.includes("temporarily paused")) {
      return "Verification requests are paused for a moment. Please try again shortly.";
    }
    if (normalized.includes("too many verification attempts") || normalized.includes("too many verification requests")) {
      return "We’re seeing a lot of traffic. Wait a minute and try the link again.";
    }
    return message || "Login link could not be verified.";
  };

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const token = params.get("token");

    // Preview mode for design iteration - skip auth flow
    // Usage: /magic-link?preview=success | error | verifying
    const preview = params.get("preview");
    if (preview === "success" || preview === "error" || preview === "verifying") {
      setStatus(preview);
      if (preview === "error") {
        setError("This is a preview of the error state.");
      }
      if (preview === "success") {
        setRedirectProgress(65); // Show partial progress, don't auto-redirect
      }
      return;
    }

    if (!token) {
      setStatus("error");
      setError("Login link is invalid or missing.");
      return;
    }

    const storageKey = `magic-link:${token}`;
    const storedState = typeof window !== "undefined" ? sessionStorage.getItem(storageKey) : null;

    if (storedState === "success") {
      setStatus("success");
      startRedirect(isExtensionAuthFlow ? 2400 : 600);
      return;
    }

    let cancelled = false;
    const getVerificationPromise = () => {
      if (!verificationPromises.current[token]) {
        if (typeof window !== "undefined") {
          sessionStorage.setItem(storageKey, "in-flight");
        }
        verificationPromises.current[token] = completeMagicLogin(token, extensionAuthOptions || undefined);
      }
      return verificationPromises.current[token];
    };

    getVerificationPromise()
      .then(() => {
        if (typeof window !== "undefined") {
          sessionStorage.setItem(storageKey, "success");
        }
        if (cancelled) return;
        setStatus("success");
        startRedirect(isExtensionAuthFlow ? 2400 : 800);
      })
      .catch((err) => {
        const alreadyUsed =
          err?.message && err.message.toLowerCase().includes("already used");
        const wasSuccess =
          typeof window !== "undefined" && sessionStorage.getItem(storageKey) === "success";

        if (typeof window !== "undefined" && !wasSuccess) {
          sessionStorage.removeItem(storageKey);
        }

        if (cancelled) return;

        if (alreadyUsed && wasSuccess) {
          // Strict Mode/dev double-invoke: treat already-used as success if we just succeeded.
          setStatus("success");
          setTimeout(() => navigate("/dashboard", { replace: true }), isExtensionAuthFlow ? 1800 : 400);
          return;
        }

        setError(getFriendlyVerifyError(err.message));
        setStatus("error");
      })
      .finally(() => {
        delete verificationPromises.current[token];
      });

    return () => {
      cancelled = true;
    };
  }, [completeMagicLogin, extensionAuthOptions, isExtensionAuthFlow, location.search, navigate]);

  useEffect(() => {
    return () => {
      if (redirectTimer.current) clearTimeout(redirectTimer.current);
      if (redirectAnimationFrame.current) cancelAnimationFrame(redirectAnimationFrame.current);
    };
  }, []);

  const startRedirect = (delay = 800) => {
    if (redirectTimer.current) clearTimeout(redirectTimer.current);
    if (redirectAnimationFrame.current) cancelAnimationFrame(redirectAnimationFrame.current);

    setRedirectProgress(0);

    const start = performance.now();
    const animate = (now) => {
      const elapsed = now - start;
      const pct = Math.min(100, (elapsed / delay) * 100);
      setRedirectProgress(pct);
      if (pct < 100) {
        redirectAnimationFrame.current = requestAnimationFrame(animate);
      }
    };

    redirectAnimationFrame.current = requestAnimationFrame(animate);
    redirectTimer.current = setTimeout(() => navigate("/dashboard", { replace: true }), delay);
  };

  const renderContent = () => {
    if (status === "success") {
      return (
        <>
          <div className="w-12 h-12 rounded-full bg-emerald-500/15 border border-emerald-500/30 flex items-center justify-center mb-5">
            <FiCheckCircle className="w-6 h-6 text-emerald-500 dark:text-emerald-400" />
          </div>

          <h1 className="text-xl sm:text-2xl font-semibold text-gray-800 dark:text-zinc-100">
            Success!
          </h1>
          <p className="text-gray-500 dark:text-zinc-500 text-sm mt-2">
            {isExtensionAuthFlow ? "Extension authentication complete. You can return to your extension tab." : "Redirecting to dashboard..."}
          </p>

          <div className="w-48 mt-5">
            <div className="h-1 rounded-full bg-gray-200 dark:bg-zinc-800 overflow-hidden">
              <div
                className="h-full bg-emerald-500 dark:bg-emerald-400 transition-all duration-100"
                style={{ width: `${redirectProgress}%` }}
              />
            </div>
          </div>
        </>
      );
    }

    if (status === "error") {
      return (
        <>
          <div className="w-12 h-12 rounded-full bg-red-500/15 border border-red-500/30 flex items-center justify-center mb-5">
            <FiAlertTriangle className="w-6 h-6 text-red-500 dark:text-red-400" />
          </div>

          <h1 className="text-xl sm:text-2xl font-semibold text-gray-800 dark:text-zinc-100">
            Verification failed
          </h1>
          <p className="text-gray-500 dark:text-zinc-500 text-sm mt-2 max-w-xs">{error}</p>

          <button
            onClick={() => navigate("/signin", { replace: true })}
            className="mt-6 px-5 py-2 bg-gray-100 dark:bg-zinc-800 hover:bg-gray-200 dark:hover:bg-zinc-700 text-gray-700 dark:text-zinc-200 text-sm font-medium rounded-lg border border-gray-300 dark:border-zinc-700 transition-colors"
          >
            Back to sign in
          </button>
        </>
      );
    }

    return (
      <>
        <div className="w-12 h-12 rounded-full bg-blue-500/15 border border-blue-500/30 flex items-center justify-center mb-5">
          <svg className="w-5 h-5 text-blue-500 dark:text-blue-400 animate-spin" style={{ animationDuration: '1s' }} viewBox="0 0 24 24" fill="none">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
          </svg>
        </div>

        <h1 className="text-xl sm:text-2xl font-semibold text-gray-800 dark:text-zinc-100">
          Verifying your link
        </h1>
        <p className="text-gray-500 dark:text-zinc-500 text-sm mt-2">
          Just a moment...
        </p>
      </>
    );
  };

  return (
    <div className="min-h-screen relative overflow-hidden bg-gradient-to-br from-gray-50 via-gray-100 to-gray-50 dark:from-black dark:via-gray-950 dark:to-black text-gray-900 dark:text-white flex items-center justify-center p-4">
      {/* Ambient background gradients - matching SignIn */}
      <div className="absolute -left-24 -top-24 w-80 h-80 sm:w-96 sm:h-96 bg-blue-500/5 dark:bg-blue-500/10 blur-3xl rounded-full animate-pulse" aria-hidden="true" style={{ animationDuration: '8s' }}></div>
      <div className="absolute -right-24 -bottom-24 w-80 h-80 sm:w-96 sm:h-96 bg-cyan-500/5 dark:bg-cyan-500/10 blur-3xl rounded-full animate-pulse" aria-hidden="true" style={{ animationDuration: '10s', animationDelay: '1s' }}></div>

      <div className="relative w-full max-w-sm">
        <div className="bg-white/80 dark:bg-zinc-900/80 backdrop-blur-sm rounded-2xl border border-gray-200 dark:border-zinc-800 p-8 flex flex-col items-center text-center">
          {renderContent()}
        </div>

        <p className="text-[10px] text-gray-500 dark:text-zinc-600 text-center mt-4">
          Ultimate Web Scraper
        </p>
      </div>
    </div>
  );
};

export default MagicLinkCallback;
